import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (user) {
    navigate('/profile');
    return null;
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100'>
      {/* Header */}
      <header className='bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-100 sticky top-0 z-40'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col sm:flex-row justify-between items-center gap-4'>
          <div className='flex items-center space-x-3'>
            <div className='w-10 h-10 bg-gradient-to-br from-primary to-blue-600 rounded-xl flex items-center justify-center shadow-lg'>
              <span className='text-white font-bold text-lg'>P</span>
            </div>
            <span className='text-2xl font-bold text-gray-900 tracking-tight'>
              PartnerGPT
            </span>
          </div>
          <div className='flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 w-full sm:w-auto'>
            <Button
              variant='ghost'
              onClick={() => navigate('/login')}
              className='w-full sm:w-auto hover-lift focus-ring transition-smooth'
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              className='w-full sm:w-auto btn-enhanced hover-lift focus-ring'
            >
              Get Started
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <main className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20'>
        <div className='text-center mb-12 sm:mb-20'>
          <div className='mb-6'>
            <span className='inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20'>
              🚀 Now with Enhanced AI Capabilities
            </span>
          </div>
          <h1 className='heading-1 text-gray-900 mb-6 max-w-4xl mx-auto'>
            Welcome to{' '}
            <span className='bg-gradient-to-r from-primary to-blue-600 bg-clip-text text-transparent'>
              PartnerGPT
            </span>
          </h1>
          <p className='body-large text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-10'>
            Your AI-powered assistant for intelligent conversations, insights,
            and solutions tailored to your needs. Experience the future of
            conversational AI today.
          </p>
          <div className='flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 justify-center items-center'>
            <Button
              size='lg'
              onClick={() => navigate('/register')}
              className='w-full sm:w-auto btn-enhanced hover-lift focus-ring px-8 py-3 text-base'
            >
              Start Free Trial
              <span className='ml-2'>→</span>
            </Button>
            <Button
              size='lg'
              variant='outline'
              onClick={() => navigate('/login')}
              className='w-full sm:w-auto hover-lift focus-ring px-8 py-3 text-base'
            >
              Sign In
            </Button>
          </div>
          <p className='text-sm text-gray-500 mt-4'>
            No credit card required • Free forever plan available
          </p>
        </div>

        {/* Features */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 sm:mb-20'>
          <Card className='card-enhanced hover-lift border-0 shadow-lg'>
            <CardHeader className='pb-4'>
              <CardTitle className='flex items-start space-x-3 text-lg'>
                <div className='w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1'>
                  <span className='text-blue-600 text-xl'>💬</span>
                </div>
                <div>
                  <h3 className='font-semibold text-gray-900'>
                    Intelligent Conversations
                  </h3>
                  <p className='text-sm text-gray-500 font-normal mt-1'>
                    Advanced AI understanding
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              <p className='text-gray-600 leading-relaxed'>
                Engage in natural, contextual conversations with our advanced AI
                that understands your needs and provides meaningful responses.
              </p>
            </CardContent>
          </Card>

          <Card className='card-enhanced hover-lift border-0 shadow-lg'>
            <CardHeader className='pb-4'>
              <CardTitle className='flex items-start space-x-3 text-lg'>
                <div className='w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1'>
                  <span className='text-green-600 text-xl'>⚡</span>
                </div>
                <div>
                  <h3 className='font-semibold text-gray-900'>
                    Lightning Fast
                  </h3>
                  <p className='text-sm text-gray-500 font-normal mt-1'>
                    Instant responses
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              <p className='text-gray-600 leading-relaxed'>
                Get instant responses and solutions with our optimized AI
                architecture that processes queries in milliseconds.
              </p>
            </CardContent>
          </Card>

          <Card className='card-enhanced hover-lift border-0 shadow-lg'>
            <CardHeader className='pb-4'>
              <CardTitle className='flex items-start space-x-3 text-lg'>
                <div className='w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-1'>
                  <span className='text-purple-600 text-xl'>🔒</span>
                </div>
                <div>
                  <h3 className='font-semibold text-gray-900'>
                    Secure & Private
                  </h3>
                  <p className='text-sm text-gray-500 font-normal mt-1'>
                    Enterprise-grade security
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className='pt-0'>
              <p className='text-gray-600 leading-relaxed'>
                Your data is protected with enterprise-grade security and
                privacy measures you can trust completely.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className='text-center bg-white rounded-2xl shadow-xl p-6 sm:p-8'>
          <h2 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-4'>
            Ready to get started?
          </h2>
          <p className='text-lg sm:text-xl text-gray-600 mb-6 sm:mb-8 px-4'>
            Join thousands of users who trust PartnerGPT for their AI needs.
          </p>
          <Button
            size='lg'
            onClick={() => navigate('/register')}
            className='w-full sm:w-auto'
          >
            Create Your Account
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className='bg-gray-900 text-white py-6 sm:py-8'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='text-center'>
            <p className='text-gray-400 text-sm'>
              © 2025 partnerGPT.ai. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
